import React, { createContext, useContext, useRef, useState, useCallback } from 'react';
import SimplePeer from 'simple-peer';

interface WebRTCContextType {
  createPeer: (initiator: boolean, stream?: MediaStream) => SimplePeer.Instance;
  destroyPeer: (peer: SimplePeer.Instance) => void;
  getLocalStream: () => Promise<MediaStream | null>;
  stopLocalStream: () => void;
  localStream: MediaStream | null;
}

const WebRTCContext = createContext<WebRTCContextType | null>(null);

export const useWebRTC = () => {
  const context = useContext(WebRTCContext);
  if (!context) {
    throw new Error('useWebRTC must be used within WebRTCProvider');
  }
  return context;
};

interface WebRTCProviderProps {
  children: React.ReactNode;
  signalingServer?: string;
}

const WebRTCProvider: React.FC<WebRTCProviderProps> = ({
  children,
  signalingServer,
}) => {
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const peersRef = useRef<Map<string, SimplePeer.Instance>>(new Map());

  const getLocalStream = useCallback(async (): Promise<MediaStream | null> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      setLocalStream(stream);
      return stream;
    } catch (error) {
      console.error('Error accessing media devices:', error);
      return null;
    }
  }, []);

  const stopLocalStream = useCallback(() => {
    if (localStream) {
      localStream.getTracks().forEach((track) => track.stop());
      setLocalStream(null);
    }
  }, [localStream]);

  const createPeer = useCallback(
    (initiator: boolean, stream?: MediaStream): SimplePeer.Instance => {
      const peer = new SimplePeer({
        initiator,
        trickle: false,
        stream: stream || localStream || undefined,
      });

      const peerId = Math.random().toString(36).substr(2, 9);
      peersRef.current.set(peerId, peer);

      peer.on('error', (err) => {
        console.error('WebRTC peer error:', err);
      });

      peer.on('close', () => {
        peersRef.current.delete(peerId);
      });

      return peer;
    },
    [localStream]
  );

  const destroyPeer = useCallback((peer: SimplePeer.Instance) => {
    peer.destroy();
    // Remove from map
    for (const [id, p] of peersRef.current.entries()) {
      if (p === peer) {
        peersRef.current.delete(id);
        break;
      }
    }
  }, []);

  const value: WebRTCContextType = {
    createPeer,
    destroyPeer,
    getLocalStream,
    stopLocalStream,
    localStream,
  };

  return <WebRTCContext.Provider value={value}>{children}</WebRTCContext.Provider>;
};

export default WebRTCProvider;

